// The relative imports below carry the .js extension the rest of the portal leaves off:
// the conformance harness compiles this file with tsc and runs it on node, which needs it.
import type { FontRef, ID, Paragraph, Run, Story, TextAttrs } from '@proofroom/schema';
import { faceEmphasis, resolveFace, type StyleLookup } from './attrs.js';
import type { PMNode } from './pmJson.js';

export interface MaterialiseContext extends Pick<StyleLookup, 'paraStyles' | 'charStyles'> {
  fonts: FontRef[];
  /** id factory for paragraphs without a paraId (tests inject a deterministic one) */
  newId?: () => string;
  /**
   * The draft's own paragraphs by id. The editor cannot author paragraph formatting at all, so the
   * overrides never travel through ProseMirror; they are restored from here instead, and without
   * them the engine recomposes an overridden 24pt heading at its base style's 10pt.
   */
  base?: Record<ID, Pick<Paragraph, 'overrides'>>;
}

export const NEW_PARA_PREFIX = 'new:';
export const isNewParaId = (id: ID): boolean => id.startsWith(NEW_PARA_PREFIX);

export function mintParaId(): string {
  const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  return NEW_PARA_PREFIX + uuid;
}

/** A story's paragraphs by id, for `MaterialiseContext.base`. */
export function baseParagraphs(story: Story): Record<ID, Pick<Paragraph, 'overrides'>> {
  return Object.fromEntries(story.paragraphs.map((p) => [p.id, p]));
}

interface RunAttrs { charStyleId?: ID; fontId?: ID }

/**
 * The run's face and character style from its marks.
 *
 * A `face` mark carries the exact fontId storyToDoc read off the run, so a face the emphasis marks
 * cannot express (anything lighter than the paragraph's own) survives the round trip. It is not
 * simply trusted: the emphasis it implies is recomputed here, and only when that still matches the
 * bold and italic marks on the node is the face taken verbatim. Toggling bold in the toolbar breaks
 * the match, so the toolbar keeps the last word over what is already there; toggling it off again
 * restores the run's own face, because the mark is still on the text.
 */
function runAttrsFor(node: PMNode, baseFontId: ID, ctx: MaterialiseContext, fontsById: Record<ID, FontRef>): RunAttrs {
  const out: RunAttrs = {};
  let bold = false;
  let italic = false;
  let marked: ID | undefined;
  for (const m of node.marks ?? []) {
    if (m.type === 'bold') bold = true;
    else if (m.type === 'italic') italic = true;
    else if (m.type === 'charStyle' && typeof m.attrs?.id === 'string') out.charStyleId = m.attrs.id;
    else if (m.type === 'face' && typeof m.attrs?.fontId === 'string') marked = m.attrs.fontId;
  }
  const base = out.charStyleId ? (ctx.charStyles[out.charStyleId]?.attrs.fontId ?? baseFontId) : baseFontId;
  if (marked) {
    const em = faceEmphasis(fontsById, base, marked);
    if (em.bold === bold && em.italic === italic) {
      out.fontId = marked;
      return out;
    }
  }
  if (bold || italic) {
    const face = resolveFace(ctx.fonts, base, { bold, italic });
    if (face) out.fontId = face.id;
  }
  return out;
}

function sameAttrs(a: RunAttrs, b: RunAttrs): boolean {
  return a.charStyleId === b.charStyleId && a.fontId === b.fontId;
}

function toRun(start: number, end: number, a: RunAttrs): Run {
  const run: Run = { start, end };
  if (a.charStyleId) run.charStyleId = a.charStyleId;
  if (a.fontId) {
    const overrides: Partial<TextAttrs> = { fontId: a.fontId };
    run.overrides = overrides;
  }
  return run;
}

function nodeText(node: PMNode): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  return (node.content ?? []).map(nodeText).join('');
}

/**
 * Materialise a ProseMirror document (JSON) into a Story, following the rules in docs/API.md:
 * one Paragraph per paragraph node, text concatenated, runs from mark boundaries, bold/italic
 * become `overrides.fontId` of the family's Bold / Italic face, paragraphs without paraId get `new:<uuid>`.
 */
export function materialise(doc: PMNode, storyId: ID, frameIds: ID[], ctx: MaterialiseContext): Story {
  const paragraphs: Paragraph[] = [];
  const fontsById: Record<ID, FontRef> = {};
  for (const f of ctx.fonts) fontsById[f.id] = f;
  const firstStyleId = Object.keys(ctx.paraStyles)[0] ?? '';
  let prevStyleId = firstStyleId;
  let prevOverrides: Paragraph['overrides'];
  for (const node of doc.content ?? []) {
    if (node.type !== 'paragraph') continue;
    const attrs = node.attrs ?? {};
    const id = typeof attrs.paraId === 'string' && attrs.paraId ? attrs.paraId : (ctx.newId ?? mintParaId)();
    const styleId = typeof attrs.styleId === 'string' && attrs.styleId ? attrs.styleId : prevStyleId;
    prevStyleId = styleId;
    const baseFontId = ctx.paraStyles[styleId]?.attrs.fontId ?? 'HelveticaNeue';
    let text = '';
    const runs: Run[] = [];
    let open: { start: number; attrs: RunAttrs } | undefined;
    for (const child of node.content ?? []) {
      const t = nodeText(child);
      if (!t) continue;
      const a = runAttrsFor(child, baseFontId, ctx, fontsById);
      if (open && sameAttrs(open.attrs, a)) {
        text += t;
        continue;
      }
      if (open) runs.push(toRun(open.start, text.length, open.attrs));
      open = { start: text.length, attrs: a };
      text += t;
    }
    if (open) runs.push(toRun(open.start, text.length, open.attrs));
    // same convention as the server's materialiser: an empty paragraph keeps one empty run
    if (runs.length === 0) runs.push({ start: 0, end: 0 });
    // a paragraph split off in the editor keeps the formatting it was split from, as its styleId does
    const overrides = ctx.base?.[id]?.overrides ?? (isNewParaId(id) ? prevOverrides : undefined);
    prevOverrides = overrides;
    paragraphs.push(overrides ? { id, styleId, text, runs, overrides } : { id, styleId, text, runs });
  }
  return { id: storyId, frameIds: [...frameIds], paragraphs };
}

/** True when two stories differ in paragraph ids, text, or style. Used to decide what to send. */
export function storiesDiffer(a: Story, b: Story): boolean {
  if (a.paragraphs.length !== b.paragraphs.length) return true;
  for (let i = 0; i < a.paragraphs.length; i++) {
    const p = a.paragraphs[i], q = b.paragraphs[i];
    if (p.id !== q.id || p.text !== q.text || p.styleId !== q.styleId) return true;
  }
  return false;
}
